import React, { useRef, useState, useEffect } from "react";
import {
  OverlayContainer,
  Column,
  Block,
  BlockContent,
  Image,
  ImageBlocks,
  FrostBackdrop,
  TextContainer,
  PDPTile,
  PDPHeading,
  PDPNumber,
  Headline,
  Subheading,
  LogoContainer,
  PLPContent,
  PDPContent,
  PDPContentBox,
  ReturnAnchor,
  ColumnContent,
  PDPTileImage,
  PDPTileWrapper,
  PDPRomanNumber,
  PLPHeadline,
  PDPSubheading,
  GantCTA,
  Link,
} from "../styles/components/PDP/Global";
import { BREAKPOINTS } from "../styles/variables/Breakpoints";
import gsap from "./Animations";

const PDPLanding = ({
  activePDP,
  activatePDP,
  parentContainer,
  enablePLPScroll,
}) => {
  const pdpImagesRef = useRef(null);
  const pdpContentRef = useRef(null);

  const [loadedContent, setLoadedContent] = useState(false);

  const contentKeys = Object.keys(activePDP.pdpContent);
  const lastKey = contentKeys[contentKeys.length - 1];
  const secondToLastKey = contentKeys[contentKeys.length - 2];

  const mm = gsap.matchMedia();
  let active = true;

  const updateClassList = (elements, addClass, removeClasses = []) => {
    elements.forEach(el => {
      removeClasses.forEach(className => {
        el.classList.remove(className);
      });
      el.classList.add(addClass);
    });
  };

  const handleHoverEnter = (activeRef, inactiveRef) => {
    if (!loadedContent || !active) return;

    const activeClass = "expanded";
    const inactiveClass = "shrinked";

    mm.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
      gsap
        .timeline({
          onStart: () => {
            updateClassList(
              activeRef.current.querySelectorAll(".column"),
              activeClass,
              [inactiveClass]
            );
            updateClassList(
              inactiveRef.current.querySelectorAll(".column"),
              inactiveClass,
              [activeClass]
            );
          },
        })
        .to(
          activeRef.current,
          {
            width: "70vw",
            duration: 0.5,
          },
          0
        )
        .to(
          inactiveRef.current,
          {
            width: "30vw",
            duration: 0.5,
          },
          0
        );
    });
  };

  const handleHoverLeave = () => {
    if (!loadedContent || !active) return;

    mm.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
      gsap
        .timeline({
          onStart: () => {
            const columns = document.querySelectorAll(".column");
            columns.forEach(el => {
              el.classList.remove("expanded", "shrinked");
            });
          },
        })
        .to(
          pdpImagesRef.current,
          {
            width: "50vw",
            duration: 0.5,
          },
          0
        )
        .to(
          pdpContentRef.current,
          {
            width: "50vw",
            duration: 0.5,
          },
          0
        );
    });
  };

  const handeScrollBack = () => {
    active = false;
    enablePLPScroll();
  };

  const intToRoman = num => {
    const lookup = {
      M: 1000,
      CM: 900,
      D: 500,
      CD: 400,
      C: 100,
      XC: 90,
      L: 50,
      XL: 40,
      X: 10,
      IX: 9,
      V: 5,
      IV: 4,
      I: 1,
    };
    let roman = "";
    for (let i in lookup) {
      while (num >= lookup[i]) {
        roman += i;
        num -= lookup[i];
      }
    }
    return roman;
  };

  const filterContentByTitle = (content, title) =>
    content.filter(pdp => pdp.moduleTitle === title);

  const RenderArchivePieve = ({ count, images, content }) => {
    const tileRef = useRef(null);
    if (!content || !images) {
      return null;
    }

    setTimeout(() => {
      if (count == 0) return;
      gsap.parallax(tileRef.current);
    }, 1000);

    return (
      <>
        <PDPTileImage
          ref={tileRef}
          $count={count}
          $background={activePDP.jsonStyles.colour}
        >
          <PDPRomanNumber $difference={count === 1} className="roman-count">
            {intToRoman(count)}.
          </PDPRomanNumber>
          {images.map((item, index) =>
            item.images.map((image, i) => (
              <React.Fragment key={`${index}-${i}`}>
                <Image
                  $desktop={image.imageMobile}
                  src={image.image.fluid.src}
                />
                {image.imageMobile && (
                  <Image $mobile src={image.imageMobile.fluid.src} />
                )}
              </React.Fragment>
            ))
          )}
        </PDPTileImage>
        {content && content.length > 0 && (
          <PDPContentBox $count={count}>
            <PDPContent $count={count}>
              {content.map((item, i) => (
                <PDPSubheading
                  key={i}
                  dangerouslySetInnerHTML={{ __html: item.text }}
                />
              ))}
            </PDPContent>
          </PDPContentBox>
        )}
        {count == 0 && (
          <FrostBackdrop
            $justify="center"
            $align="flex-start"
            className="column"
          >
            <TextContainer $introduction={count == 0}>
              <PDPNumber>
                {activePDP.plpParagraphs && (
                  <Subheading
                    $whiteWrap
                    className="column"
                    dangerouslySetInnerHTML={{
                      __html: activePDP.plpParagraphs[0].text,
                    }}
                  />
                )}
              </PDPNumber>
              <PDPHeading>
                {activePDP.plpHeadlines && (
                  <Headline
                    className="column"
                    dangerouslySetInnerHTML={{
                      __html: activePDP.plpHeadlines[0].text,
                    }}
                  />
                )}
              </PDPHeading>
            </TextContainer>
          </FrostBackdrop>
        )}
      </>
    );
  };

  const RenderHeadline = ({ content }) => {
    if (!content || content.length === 0) {
      return null;
    }

    return (
      <>
        {content.map((item, i) => (
          <PLPHeadline
            key={i}
            className="column expanded"
            dangerouslySetInnerHTML={{ __html: item.text }}
          />
        ))}
      </>
    );
  };

  useEffect(() => {
    setTimeout(() => setLoadedContent(true), 1000);
  }, []);

  return (
    <OverlayContainer>
      <Column
        ref={pdpImagesRef}
        onMouseEnter={() => handleHoverEnter(pdpImagesRef, pdpContentRef)}
        onMouseLeave={() => handleHoverLeave()}
        $border
      >
        <ColumnContent className="column" $flexGrow>
          <Block
            $justify="flex-start"
            $align="flex-start"
            $direction="column"
            $mobileMargin
          >
            <ImageBlocks className="column">
              {Object.keys(activePDP.pdpContent).map(key => {
                const isLastKey = key === lastKey;
                const isSecondToLastKey = key === secondToLastKey;
                return (
                  <PDPTileWrapper key={key} $count={key}>
                    <PDPTile
                      $count={key}
                      $background={activePDP.jsonStyles.colour}
                      $lastKey={isLastKey}
                      $secondToLastKey={isSecondToLastKey}
                    >
                      <RenderArchivePieve
                        count={key}
                        images={filterContentByTitle(
                          activePDP.pdpContent[key].content,
                          "pdp-images"
                        )}
                        content={filterContentByTitle(
                          activePDP.pdpContent[key].content,
                          "pdp-content"
                        )}
                      />
                    </PDPTile>
                    {key == 0 && (
                      <Block $justify="flex-start" $direction="column" $mobile>
                        <BlockContent>
                          <PLPContent>
                            <LogoContainer>
                              <Image src="https://www.datocms-assets.com/40484/1710520852-gant.svg" />
                            </LogoContainer>
                            <PDPHeading $justify="flex-start">
                              <RenderHeadline
                                content={filterContentByTitle(
                                  activePDP.plpDescriptionHeading,
                                  "plp-description-heading"
                                )}
                              />
                            </PDPHeading>
                            <PDPHeading $justify="flex-start">
                              <RenderHeadline
                                content={filterContentByTitle(
                                  activePDP.plpDescription,
                                  "plp-description"
                                )}
                              />
                            </PDPHeading>
                          </PLPContent>
                        </BlockContent>
                      </Block>
                    )}
                  </PDPTileWrapper>
                );
              })}
            </ImageBlocks>
            <GantCTA>
              <Subheading $centerAlign>
                Want to see more of what GANT is up to today? Check out their
                current collections <Link href="https://gant.com/">here</Link>.
              </Subheading>
            </GantCTA>
          </Block>
        </ColumnContent>
      </Column>
      <Column
        ref={pdpContentRef}
        onMouseEnter={() => handleHoverEnter(pdpContentRef, pdpImagesRef)}
        onMouseLeave={() => handleHoverLeave()}
        $positionColumn
        $desktop
      >
        <ColumnContent>
          <Block $justify="flex-start" $direction="column">
            <BlockContent>
              <PLPContent>
                <ReturnAnchor onClick={handeScrollBack}>
                  <Headline>☜ Back to Archive List</Headline>
                </ReturnAnchor>
                <PDPHeading $justify="flex-start">
                  <RenderHeadline
                    content={filterContentByTitle(
                      activePDP.plpDescriptionHeading,
                      "plp-description-heading"
                    )}
                  />
                </PDPHeading>
                <LogoContainer>
                  <Image src="https://www.datocms-assets.com/40484/1710520852-gant.svg" />
                </LogoContainer>
                <PDPHeading $justify="flex-start" $margin>
                  <RenderHeadline
                    content={filterContentByTitle(
                      activePDP.plpDescription,
                      "plp-description"
                    )}
                  />
                </PDPHeading>
              </PLPContent>
            </BlockContent>
          </Block>
        </ColumnContent>
      </Column>
    </OverlayContainer>
  );
};

export default PDPLanding;
